/**
 * useBehavioralSignals — fire-and-forget wrapper around the
 * `log-behavioral-signal` edge function.
 *
 * The edge function:
 *   - writes a behavioral_risk_signals row (server-only table)
 *   - surfaces a moderation_log nudge to the member (so the member's
 *     UI can react in real time via useSafetyAlerts)
 *   - auto-escalates to mod_review_queue when the rolling 7-day weight
 *     crosses the threshold
 *
 * Always called as fire-and-forget so the user's send/swipe path is
 * never blocked.
 */
import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export type BehavioralSignalKind =
  | "rapid_messaging"
  | "off_platform_request"
  | "financial_request"
  | "sexual_pressure"
  | "meet_now_pressure"
  | "identity_inconsistency"
  | "geo_anomaly"
  | "duplicate_account_signal"
  | "reported_pattern"
  | "model_flag";

export interface BehavioralSignalArgs {
  kind: BehavioralSignalKind;
  weight?: number;
  evidence?: Record<string, unknown> | null;
  conversationId?: string | null;
  messageId?: string | null;
}

export const logBehavioralSignal = (args: BehavioralSignalArgs): void => {
  void supabase.functions
    .invoke("log-behavioral-signal", {
      body: {
        kind: args.kind,
        weight: args.weight,
        evidence: args.evidence ?? null,
        conversation_id: args.conversationId ?? null,
        message_id: args.messageId ?? null,
      },
    })
    .catch(() => {
      /* fail-open per spec — never block sends */
    });
};

export const useBehavioralSignals = () => {
  const log = useCallback((args: BehavioralSignalArgs) => logBehavioralSignal(args), []);
  return { log };
};
